import { makeRequest } from "./client";
import type { Notice, NoticeRead } from "./types";
import type { PollResultsResponse } from "../../domain/polls/types";

type CreateNoticePayload = Omit<
  Notice,
  "id" | "createdAt" | "readCount" | "noticeId"
> & {
  suppressNotifications?: boolean;
  excludeFromFeed?: boolean;
  createdAtOverride?: string;
  uploadedFiles?: Array<{
    fileUrl: string;
    fileName: string;
    contentType: string;
    size: number;
  }>;
};

// Forward reference — will be set by index.ts to avoid circular deps
let _apiRef: any = null;
export const setApiRef = (ref: any) => { _apiRef = ref; };

export const noticesApi = {
  getAll: async (
    _schoolId: string,
    token: string,
    options?: { excludeFeedHidden?: boolean }
  ): Promise<Notice[]> => {
    // Org scope comes from the bearer token (notices-get uses getAuthContext).
    const headers = { Authorization: `Bearer ${token}` };
    const querySuffix = options?.excludeFeedHidden
      ? "?excludeFeedHidden=true"
      : "";

    const response = await makeRequest<{ notices: Notice[] }>(
      `/v1/notices${querySuffix}`,
      { headers }
    );
    return response.notices || [];
  },

  getPresignedUrl: async (
    fileName: string,
    contentType: string,
    organizationId: string,
    noticeId?: string,
    token?: string
  ): Promise<{ presignedUrl: string; fileUrl: string; fileKey: string }> => {
    return makeRequest<{
      presignedUrl: string;
      fileUrl: string;
      fileKey: string;
    }>("/v1/notices/presigned-url", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: JSON.stringify({
        fileName,
        contentType,
        organizationId,
        noticeId,
      }),
    });
  },

  create: async (
    notice: CreateNoticePayload,
    token: string
  ): Promise<{ message: string; notice: { noticeId: string } }> => {
    const userData = localStorage.getItem("user");
    let organizationId: string | undefined;

    if (userData) {
      try {
        const user = JSON.parse(userData);
        organizationId = user.schoolId || user.orgId;
      } catch {
        // Ignore malformed cached user payload.
      }
    }

    const requestBody: any = {
      ...notice,
      organizationId: notice.targetSubcircle ? undefined : organizationId,
    };

    if ((notice as any).eventBackgroundImage !== undefined) {
      requestBody.eventBackgroundImage = (notice as any).eventBackgroundImage;
    }
    if ((notice as any).eventFontColor !== undefined) {
      requestBody.eventFontColor = (notice as any).eventFontColor;
    }

    return makeRequest("/v1/notices", {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body: JSON.stringify(requestBody),
    });
  },

  getById: async (id: string, token: string): Promise<Notice> => {
    const allNotices = await (_apiRef?.notices?.getAll ?? noticesApi.getAll)("", token);
    const notice = allNotices.find((n: Notice) => n.noticeId === id);
    if (!notice) {
      throw new Error("Notice not found");
    }
    return notice;
  },

  createDraft: async (
    notice: CreateNoticePayload,
    token: string
  ): Promise<Notice> => {
    const userData = localStorage.getItem("user");
    let organizationId: string | undefined;

    if (userData) {
      try {
        const user = JSON.parse(userData);
        organizationId = user.schoolId || user.orgId;
      } catch {
        // Ignore malformed cached user payload.
      }
    }

    const requestBody: any = {
      ...notice,
      organizationId: notice.targetSubcircle ? undefined : organizationId,
    };

    if ((notice as any).eventBackgroundImage !== undefined) {
      requestBody.eventBackgroundImage = (notice as any).eventBackgroundImage;
    }
    if ((notice as any).eventFontColor !== undefined) {
      requestBody.eventFontColor = (notice as any).eventFontColor;
    }

    const response = await makeRequest<{ notice: Notice }>("/v1/notices/draft", {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body: JSON.stringify(requestBody),
    });

    return response.notice;
  },

  getReads: async (
    noticeId: string,
    schoolId: string,
    token: string,
    options?: { targetSubcircle?: string }
  ): Promise<NoticeRead> => {
    const params = new URLSearchParams({ noticeId });
    if (options?.targetSubcircle?.trim()) {
      params.set("subcircleId", options.targetSubcircle.trim());
    }
    if (schoolId?.trim()) {
      params.set("organizationId", schoolId.trim());
    }

    const response = await makeRequest<{
      noticeId: string;
      views: Array<{
        userId: string;
        name: string;
        email: string;
        readAt: string;
      }>;
      seenCount?: number;
      audienceTotal?: number;
      totalViews: number;
    }>(`/v1/notices/get-views?${params.toString()}`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    const seen = response.seenCount ?? response.views?.length ?? 0;
    const total =
      response.audienceTotal ?? response.totalViews ?? seen;

    return {
      total,
      seen,
      users: response.views.map((view) => ({
        id: view.userId,
        name: view.name,
        email: view.email,
      })),
    };
  },

  delete: async (
    noticeId: string,
    schoolId: string,
    token: string,
    /** series: every date of a repeating Event. planner: every Event in its Planner, and the Planner. */
    options?: { scope?: "series" | "planner" }
  ): Promise<{
    deletedCount?: number;
    deletedNoticeIds?: string[];
    eventSeriesId?: string;
    plannerId?: string;
  }> => {
    const query = options?.scope ? `?scope=${options.scope}` : "";
    return makeRequest(`/v1/org/${schoolId}/notices/${noticeId}${query}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    });
  },

  /** Soft-delete every notice in a recurring series (server bulk + per-id fallback). */
  deleteSeries: async (
    notices: Notice[],
    anchorNoticeId: string,
    eventSeriesId: string,
    schoolId: string,
    token: string
  ): Promise<string[]> => {
    const memberIds = Array.from(
      new Set(
        notices
          .filter((n) => n.eventSeriesId === eventSeriesId)
          .map((n) => n.noticeId || n.id)
          .filter((id): id is string => Boolean(id))
      )
    );

    if (!memberIds.includes(anchorNoticeId)) {
      memberIds.push(anchorNoticeId);
    }

    let deletedIds = new Set<string>();

    try {
      const result = await noticesApi.delete(anchorNoticeId, schoolId, token, {
        scope: "series",
      });
      if (result.deletedNoticeIds?.length) {
        deletedIds = new Set(result.deletedNoticeIds);
      } else {
        deletedIds.add(anchorNoticeId);
      }

      const deletedCount = result.deletedCount ?? deletedIds.size;
      if (deletedCount >= memberIds.length) {
        return Array.from(deletedIds);
      }
    } catch {
      // Older backend without scope=series — fall through to per-notice deletes.
    }

    const remaining = memberIds.filter((id) => !deletedIds.has(id));
    await Promise.all(
      remaining.map(async (id) => {
        try {
          await noticesApi.delete(id, schoolId, token);
          deletedIds.add(id);
        } catch {
          // Best-effort; caller may refetch to verify.
        }
      })
    );

    return Array.from(deletedIds);
  },

  reschedulePublish: async (
    noticeId: string,
    publishAt: string,
    token: string
  ): Promise<Notice> => {
    return makeRequest(`/v1/notices/${noticeId}/schedule`, {
      method: "PATCH",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ publishAt }),
    });
  },

  publishScheduledNow: async (
    noticeId: string,
    token: string
  ): Promise<Notice> => {
    return makeRequest(`/v1/notices/${noticeId}/publish-now`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
    });
  },

  /** Soft-delete multiple Drive backing notices and purge S3 keys no longer referenced by any active notice. */
  bulkDriveSoftDelete: async (
    schoolId: string,
    noticeIds: string[],
    token: string
  ): Promise<void> => {
    await makeRequest(`/v1/org/${schoolId}/notices/drive/bulk-soft-delete`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ noticeIds }),
    });
  },

  /** Persist whether members are notified when files are added to a Drive folder. */
  setDriveFolderNotifyOnAdd: async (
    schoolId: string,
    token: string,
    body: {
      matchFolderKey: string;
      targetSubcircle?: string;
      notifyOnAdd: boolean;
    },
  ): Promise<{
    message: string;
    noticeId: string;
    notifyOnAdd: boolean;
  }> => {
    return makeRequest(
      `/v1/org/${schoolId}/notices/drive/folder-notify-pref`,
      {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      },
    );
  },

  /** Upsert one backing notice per Drive folder (dedupe, in-place update, purge removed S3 URLs). */
  syncDriveFolder: async (
    schoolId: string,
    token: string,
    body: {
      matchFolderKey: string;
      targetSubcircle?: string;
      title: string;
      body: string;
      type: string;
      attachments: Array<{
        name: string;
        url: string;
        type: string;
        size: number;
      }>;
      revisionSummary?: string;
      createdAtOverride?: string;
      excludeFromFeed?: boolean;
      suppressNotifications?: boolean;
    }
  ): Promise<{
    message: string;
    noticeId: string;
    created: boolean;
    dedupedCount: number;
  }> => {
    return makeRequest(`/v1/org/${schoolId}/notices/drive/folder-sync`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    });
  },

  getPollResults: async (
    noticeId: string,
    token: string
  ): Promise<PollResultsResponse> => {
    const params = new URLSearchParams({ noticeId });
    return makeRequest<PollResultsResponse>(
      `/v1/notices/poll-results?${params.toString()}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
  },

  submitPollResponse: async (
    noticeId: string,
    optionId: string,
    token: string,
    organizationId?: string
  ): Promise<{ noticeId: string; optionId: string; respondedAt: string }> => {
    return makeRequest("/v1/notices/poll-response", {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ noticeId, optionId, organizationId }),
    });
  },
};
